import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';

/**
 * QA-515 추가 — 상품 [변경 이력](고도몰 상품 수정 > 변경 이력: 등록일시·수정일시·관리자·분류·수정 전·수정 후).
 * `GET /admin/v1/products/{productId}/change-logs`(catalog #110, v1.0.67 — 그 배포 시점부터 쌓인다). 최근 것이 위.
 * 한 번 저장이 여러 분류를 바꾸면 분류마다 한 건(같은 changedAt).
 * BE 반영 전(404·501)에는 null — 화면에 "서버 반영 후" 안내를 띄운다.
 */
export interface ProductChangeItem {
  /** 바뀐 항목 표시 이름(예: "판매가", "PC쇼핑몰 노출 여부"). */
  label: string;
  before: string | null;
  after: string | null;
}

export interface ProductChangeLogEntry {
  changedAt: string;
  /** 관리자 표시 이름(이름 또는 로그인 ID). */
  adminName: string | null;
  adminLoginId: string | null;
  /** 관리자 ID — 이름·로그인 ID 조회에 실패했을 때 표시. */
  adminUserId: string | null;
  /** 저장 종류(수정·부분 수정·일괄 수정·엑셀 일괄 등록·등록·복사 등록·게시·보관·재고 조정 …). */
  action: string | null;
  /** 분류(상품정보·가격·재고·옵션·노출 …). */
  category: string | null;
  changes: ProductChangeItem[];
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
const val = (v: unknown): string | null => (v == null ? null : typeof v === 'string' ? v : JSON.stringify(v));

export const productChangeLogsKey = (productId: string) => ['admin', 'products', productId, 'change-logs'] as const;

export const useProductChangeLogsQuery = (productId: string | undefined, enabled: boolean) =>
  useQuery<ProductChangeLogEntry[] | null>({
    queryKey: productChangeLogsKey(productId ?? ''),
    enabled: Boolean(productId) && enabled,
    retry: false,
    queryFn: async () => {
      const res = await adminFetch(`/admin/v1/products/${encodeURIComponent(productId as string)}/change-logs?page=0&size=200`);
      if (res.status === 404 || res.status === 501) return null;
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as { data?: unknown };
      const raw = Array.isArray(env.data)
        ? env.data
        : Array.isArray((env.data as { items?: unknown } | null)?.items)
          ? (env.data as { items: unknown[] }).items
          : [];
      return raw.map((r) => {
        const o = (r ?? {}) as Record<string, unknown>;
        const changes = Array.isArray(o.changes) ? o.changes : [];
        return {
          changedAt: str(o.changedAt) ?? '',
          adminName: str(o.adminName),
          adminLoginId: str(o.adminLoginId),
          adminUserId: str(o.adminUserId),
          action: str(o.action),
          category: str(o.category),
          changes: changes.map((c) => {
            const ch = (c ?? {}) as Record<string, unknown>;
            return { label: str(ch.label) ?? str(ch.field) ?? '-', before: val(ch.before), after: val(ch.after) };
          }),
        };
      });
    },
  });
